import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ShiftRequestWithDetails } from "@/types";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose 
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, Clock, Plus, AlertCircle, CheckCircle, User, MessageSquare, XCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useShiftRequests } from "@/hooks/scheduling/useShiftRequests";
import { ShiftWithRequestCount } from "@/types/shift-components";
import { toast } from "@/hooks/ui/use-toast";
import { 
  AvailableShiftStatusBadge, 
  formatShiftTimeRange, 
  formatRequestTimeline 
} from "@/lib/scheduling/unified-utilities";

interface AvailableShiftsTabProps {
  employeeId: string;
  businessId: string;
}

const AvailableShiftsTab: React.FC<AvailableShiftsTabProps> = ({ employeeId, businessId }) => {
  const [availableShifts, setAvailableShifts] = useState<ShiftWithRequestCount[]>([]);
  const [loadingShifts, setLoadingShifts] = useState(true);
  const [selfAssignmentEnabled, setSelfAssignmentEnabled] = useState(false);
  const [requestNotes, setRequestNotes] = useState("");
  const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("available");

  const {
    shiftRequests,
    loadingRequests,
    createShiftRequest,
    fetchEmployeeShiftRequests,
  } = useShiftRequests({ businessId, employeeId });

  // Check if self-assignment is enabled for this business
  useEffect(() => {
    const checkSelfAssignmentSetting = async () => {
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('allow_shift_self_assignment')
          .eq('id', businessId)
          .single();

        if (error) throw error;

        setSelfAssignmentEnabled(data?.allow_shift_self_assignment || false);
      } catch (error) {
        setSelfAssignmentEnabled(false);
      }
    };

    if (businessId) {
      checkSelfAssignmentSetting();
    }
  }, [businessId]);

  // Fetch available shifts (unassigned and in the future)
  useEffect(() => {
    const fetchAvailableShifts = async () => {
      if (!businessId) return;

      setLoadingShifts(true);
      try {
        const now = new Date();
        const { data, error } = await supabase
          .from('shifts')
          .select(`
            *,
            employees:employee_id(name, email)
          `)
          .eq('business_id', businessId)
          .is('employee_id', null) // Only unassigned shifts
          .gte('start_time', now.toISOString()) // Only future shifts
          .order('start_time', { ascending: true });

        if (error) throw error;

        // Get request counts for each shift
        const shiftsWithRequestCounts = await Promise.all(
          (data || []).map(async (shift) => {
            const { data: requestsData, error: requestsError } = await supabase
              .from('shift_requests')
              .select('id')
              .eq('shift_id', shift.id)
              .eq('status', 'pending');

            if (requestsError) {
              return { ...shift, request_count: 0 };
            }

            return { ...shift, request_count: requestsData?.length || 0 };
          })
        );

        setAvailableShifts(shiftsWithRequestCounts);
      } catch (error) {
        toast.error("Error", {
          description: "Failed to load available shifts",
        });
      } finally {
        setLoadingShifts(false);
      }
    };

    fetchAvailableShifts();
  }, [businessId]);

  // Fetch employee's shift requests
  useEffect(() => {
    if (employeeId) {
      fetchEmployeeShiftRequests(employeeId);
    }
  }, [employeeId, fetchEmployeeShiftRequests]);

  const handleRequestShift = async (shiftId: string, notes?: string) => {
    await createShiftRequest(shiftId, notes);
    setRequestNotes("");
    setSelectedShiftId(null);
    
    // Refresh available shifts to update request counts
    const { data, error } = await supabase
      .from('shifts')
      .select(`
        *,
        employees:employee_id(name, email)
      `)
      .eq('business_id', businessId)
      .is('employee_id', null)
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true });

    if (!error && data) {
      const shiftsWithRequestCounts = await Promise.all(
        data.map(async (shift) => {
          const { data: requestsData, error: requestsError } = await supabase
            .from('shift_requests')
            .select('id')
            .eq('shift_id', shift.id)
            .eq('status', 'pending');

          return { ...shift, request_count: requestsData?.length || 0 };
        })
      );
      setAvailableShifts(shiftsWithRequestCounts);
    }
  };

  const hasRequestedShift = (shiftId: string) => {
    return shiftRequests.some(
      request => request.shift_id === shiftId && 
      ['pending', 'approved'].includes(request.status || '')
    );
  };

  const getRequestStatus = (shiftId: string) => {
    const request = shiftRequests.find(
      req => req.shift_id === shiftId && 
      ['pending', 'approved', 'rejected'].includes(req.status || '')
    );
    return request?.status || null;
  };

  // Status badge and time formatting now handled by unified utilities

  if (!selfAssignmentEnabled) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Available Shifts</h2>
          <p className="text-muted-foreground">Request assignment to available shifts</p>
        </div>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Shift self-assignment is not enabled for your business. Contact your manager to enable this feature.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Only show loading state when there's no existing data
  // This keeps the UI visible during background refetches
  if (loadingShifts && availableShifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading available shifts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Shift Requests</h2>
        <p className="text-muted-foreground">Request assignment to available shifts and track your requests</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="available" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Available Shifts
          </TabsTrigger>
          <TabsTrigger value="my-requests" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            My Requests ({shiftRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="space-y-4">
          {availableShifts.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CalendarDays className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Available Shifts</h3>
                <p className="text-muted-foreground text-center">
                  There are no unassigned shifts available for request at the moment. Check back later or contact your manager.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {availableShifts.map((shift) => (
                <ShiftCard
                  key={shift.id}
                  shift={shift}
                  onRequestShift={handleRequestShift}
                  hasRequested={hasRequestedShift(shift.id)}
                  requestStatus={getRequestStatus(shift.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="my-requests" className="space-y-4">
          {loadingRequests ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-muted-foreground">Loading your requests...</p>
              </div>
            </div>
          ) : shiftRequests.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Clock className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Requests Yet</h3>
                <p className="text-muted-foreground text-center">
                  You haven't submitted any shift requests yet. Check the Available Shifts tab to request shifts.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {shiftRequests.map((request) => (
                <MyRequestCard
                  key={request.id}
                  request={request}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface ShiftCardProps {
  shift: ShiftWithRequestCount;
  onRequestShift: (shiftId: string, notes?: string) => Promise<void>;
  hasRequested: boolean;
  requestStatus: string | null;
}

const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  onRequestShift,
  hasRequested,
  requestStatus,
}) => {
  const [requestNotes, setRequestNotes] = useState("");

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <CalendarDays className="w-5 h-5 text-primary" />
            <div>
              <CardTitle className="text-sm font-medium">
                {shift.title || "Untitled Shift"}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {formatShiftTimeRange(shift.start_time, shift.end_time)}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <AvailableShiftStatusBadge status={requestStatus} />
            {shift.request_count > 0 && (
              <Badge variant="outline" className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {shift.request_count} request{shift.request_count !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {shift.notes && (
          <div className="mb-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">{shift.notes}</p>
          </div>
        )}

        {!hasRequested ? (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Request This Shift
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Request Shift Assignment</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-1">{shift.title || "Untitled Shift"}</h4>
                  <p className="text-sm text-muted-foreground">
                    {formatShiftTimeRange(shift.start_time, shift.end_time)}
                  </p>
                </div>
                <div>
                  <Label htmlFor="request-notes">Message to Manager (Optional)</Label>
                  <Textarea
                    id="request-notes"
                    placeholder="Add any notes about your availability or reason for requesting this shift..."
                    value={requestNotes}
                    onChange={(e) => setRequestNotes(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Your request will be sent to your manager for approval. You'll be notified of their decision.
                </p>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button onClick={() => onRequestShift(shift.id, requestNotes)}>
                    Submit Request
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Button disabled className="w-full">
            <CheckCircle className="w-4 h-4 mr-2" />
            Request Submitted
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

interface MyRequestCardProps {
  request: {
    id: string;
    status: string;
    reason: string | null;
    created_at: string;
    shifts: {
      start_time: string;
      end_time: string;
      title: string | null;
    } | null;
  };
  formatShiftTime: (startTime: string, endTime: string) => string;
  getStatusBadge: (status: string | null) => React.ReactNode;
}

const MyRequestCard: React.FC<MyRequestCardProps> = ({
  request,
  formatShiftTime,
  getStatusBadge,
}) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <CalendarDays className="w-5 h-5 text-primary" />
            <div>
              <CardTitle className="text-sm font-medium">
                {request.shifts?.title || "Untitled Shift"}
              </CardTitle>
              {request.shifts && (
                <p className="text-sm text-muted-foreground">
                  {formatShiftTime(request.shifts.start_time, request.shifts.end_time)}
                </p>
              )}
            </div>
          </div>
          {getStatusBadge(request.status)}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Employee Notes */}
        {request.employee_notes && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Your Message</span>
            </div>
            <p className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3">
              {request.employee_notes}
            </p>
          </div>
        )}

        {/* Manager Response */}
        {request.manager_notes && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Manager Response</span>
            </div>
            <p className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3">
              {request.manager_notes}
            </p>
          </div>
        )}

        {/* Request Timeline */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>
            Requested {format(new Date(request.requested_at || request.created_at || ''), 'MMM dd, yyyy • h:mm a')}
          </span>
          {request.reviewed_at && (
            <span>
              • Reviewed {format(new Date(request.reviewed_at), 'MMM dd, yyyy • h:mm a')}
            </span>
          )}
        </div>

        {/* Status-specific messages */}
        {request.status === 'approved' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Request Approved</span>
            </div>
            <p className="text-sm text-green-700 mt-1">
              You've been assigned to this shift. Check your schedule for details.
            </p>
          </div>
        )}
        
        {request.status === 'rejected' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">Request Rejected</span>
            </div>
            <p className="text-sm text-red-700 mt-1">
              Your request was not approved. {request.manager_notes ? 'See manager response above.' : 'Contact your manager for more details.'}
            </p>
          </div>
        )}
        
        {request.status === 'pending' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Request Pending</span>
            </div>
            <p className="text-sm text-blue-700 mt-1">
              Your request is waiting for manager approval. You'll be notified of the decision.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailableShiftsTab;